import { useState } from 'react';
import { Button } from '@/components/ui';
import { useProjectCustomers } from '../hooks/useProjectCustomers';
import { NewCustomerForm } from './NewCustomerForm';
import type { NewCustomerFormData } from './NewCustomerForm';
import { PROJECT_CUSTOMER_ROLES } from '../types';
import type { ProjectCustomerView, ProjectCustomerRole } from '../types';

interface ProjectCustomersSectionProps {
  projectId: string;
  businessId: string;
  disabled?: boolean;
}

export function ProjectCustomersSection({
  projectId,
  businessId,
  disabled = false,
}: ProjectCustomersSectionProps) {
  const {
    customers,
    availableCustomers,
    isLoading,
    isUpdating,
    error,
    addCustomer,
    createAndAddCustomer,
    removeCustomer,
    setPrimary,
    setBilling,
  } = useProjectCustomers({
    projectId,
    businessId,
  });

  const [isAddingCustomer, setIsAddingCustomer] = useState(false);
  const [addMode, setAddMode] = useState<'select' | 'create'>('select');
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');
  const [selectedRole, setSelectedRole] = useState<ProjectCustomerRole>('contact');
  const [setAsBilling, setSetAsBilling] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);

  const handleAddCustomer = async () => {
    if (!selectedCustomerId) return;

    const success = await addCustomer(selectedCustomerId, selectedRole, setAsBilling);
    if (success) {
      resetAddState();
    }
  };

  const handleCreateCustomer = async (data: NewCustomerFormData) => {
    setCreateError(null);
    const success = await createAndAddCustomer(
      {
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        phones: data.phones,
        billing_street_address: data.billing_street_address,
        billing_unit: data.billing_unit || undefined,
        billing_city: data.billing_city,
        billing_province: data.billing_province,
        billing_postal_code: data.billing_postal_code,
        billing_country: data.billing_country,
      },
      data.role,
      data.is_billing
    );
    if (success) {
      resetAddState();
    }
  };

  const resetAddState = () => {
    setIsAddingCustomer(false);
    setAddMode('select');
    setSelectedCustomerId('');
    setSelectedRole('contact');
    setSetAsBilling(false);
    setCreateError(null);
  };

  const handleRemoveCustomer = async (customerId: string) => {
    const success = await removeCustomer(customerId);
    if (success) {
      setConfirmRemove(null);
    }
  };

  const handleSetPrimary = async (projectCustomerId: string) => {
    await setPrimary(projectCustomerId);
  };

  const handleSetBilling = async (projectCustomerId: string) => {
    await setBilling(projectCustomerId);
  };

  const getRoleBadgeColor = (role: ProjectCustomerRole, isBilling: boolean) => {
    if (role === 'primary') return 'bg-primary-100 text-primary-700';
    if (isBilling) return 'bg-green-100 text-green-700';
    if (role === 'billing') return 'bg-green-100 text-green-700';
    if (role === 'stakeholder') return 'bg-purple-100 text-purple-700';
    return 'bg-secondary-100 text-secondary-700';
  };

  const getRoleLabel = (role: ProjectCustomerRole) => {
    return PROJECT_CUSTOMER_ROLES.find((r) => r.value === role)?.label || role;
  };

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-3">
        <div className="h-16 bg-secondary-100 rounded-lg" />
        <div className="h-16 bg-secondary-100 rounded-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Error Message */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Customer List */}
      <div className="space-y-3">
        {customers.map((customer) => (
          <CustomerCard
            key={customer.id}
            customer={customer}
            isUpdating={isUpdating}
            disabled={disabled}
            isConfirmingRemove={confirmRemove === customer.customer_id}
            onSetPrimary={() => handleSetPrimary(customer.id)}
            onSetBilling={() => handleSetBilling(customer.id)}
            onRemoveClick={() => setConfirmRemove(customer.customer_id)}
            onRemoveConfirm={() => handleRemoveCustomer(customer.customer_id)}
            onRemoveCancel={() => setConfirmRemove(null)}
            getRoleBadgeColor={getRoleBadgeColor}
            getRoleLabel={getRoleLabel}
            canRemove={customers.length > 1}
          />
        ))}
      </div>

      {/* Add Customer Section */}
      {!isAddingCustomer ? (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsAddingCustomer(true)}
          disabled={disabled}
        >
          <svg
            className="w-4 h-4 mr-1.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 6v6m0 0v6m0-6h6m-6 0H6"
            />
          </svg>
          Add Customer
        </Button>
      ) : (
        <div className="p-4 bg-secondary-50 rounded-lg border border-secondary-200 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-secondary-900">Add Customer to Project</h4>
            <button
              onClick={resetAddState}
              className="p-1 text-secondary-400 hover:text-secondary-600 rounded"
              disabled={isUpdating}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Mode Toggle */}
          <div className="flex border border-secondary-300 rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => setAddMode('select')}
              className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${
                addMode === 'select'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-secondary-700 hover:bg-secondary-50'
              }`}
              disabled={isUpdating}
            >
              Select Existing
            </button>
            <button
              type="button"
              onClick={() => setAddMode('create')}
              className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${
                addMode === 'create'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-secondary-700 hover:bg-secondary-50'
              }`}
              disabled={isUpdating}
            >
              Create New
            </button>
          </div>

          {addMode === 'select' ? (
            <>
              {/* Customer Select */}
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Customer
                </label>
                {availableCustomers.length > 0 ? (
                  <select
                    value={selectedCustomerId}
                    onChange={(e) => setSelectedCustomerId(e.target.value)}
                    className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    disabled={isUpdating}
                  >
                    <option value="">Select a customer...</option>
                    {availableCustomers.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.first_name} {c.last_name}
                        {c.email && ` (${c.email})`}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="text-sm text-secondary-500 py-2">
                    No available customers. All customers are already added to this project.
                  </p>
                )}
              </div>

              {availableCustomers.length > 0 && (
                <>
                  {/* Role Select */}
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Role
                    </label>
                    <select
                      value={selectedRole}
                      onChange={(e) => setSelectedRole(e.target.value as ProjectCustomerRole)}
                      className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      disabled={isUpdating}
                    >
                      {PROJECT_CUSTOMER_ROLES.filter((r) => r.value !== 'primary').map((role) => (
                        <option key={role.value} value={role.value}>
                          {role.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Billing Checkbox */}
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={setAsBilling}
                      onChange={(e) => setSetAsBilling(e.target.checked)}
                      className="w-4 h-4 text-primary-600 border-secondary-300 rounded focus:ring-primary-500"
                      disabled={isUpdating}
                    />
                    <span className="text-sm text-secondary-700">
                      Set as billing contact
                    </span>
                  </label>

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={resetAddState}
                      disabled={isUpdating}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleAddCustomer}
                      disabled={!selectedCustomerId || isUpdating}
                      isLoading={isUpdating}
                    >
                      Add
                    </Button>
                  </div>
                </>
              )}
            </>
          ) : (
            <NewCustomerForm
              onSubmit={handleCreateCustomer}
              onCancel={resetAddState}
              isSubmitting={isUpdating}
              error={createError || error}
            />
          )}
        </div>
      )}

      {/* Empty State */}
      {customers.length === 0 && !isLoading && (
        <p className="text-sm text-secondary-500 text-center py-4">
          No customers assigned to this project
        </p>
      )}
    </div>
  );
}

// Customer Card Sub-component
interface CustomerCardProps {
  customer: ProjectCustomerView;
  isUpdating: boolean;
  disabled: boolean;
  isConfirmingRemove: boolean;
  onSetPrimary: () => void;
  onSetBilling: () => void;
  onRemoveClick: () => void;
  onRemoveConfirm: () => void;
  onRemoveCancel: () => void;
  getRoleBadgeColor: (role: ProjectCustomerRole, isBilling: boolean) => string;
  getRoleLabel: (role: ProjectCustomerRole) => string;
  canRemove: boolean;
}

function CustomerCard({
  customer,
  isUpdating,
  disabled,
  isConfirmingRemove,
  onSetPrimary,
  onSetBilling,
  onRemoveClick,
  onRemoveConfirm,
  onRemoveCancel,
  getRoleBadgeColor,
  getRoleLabel,
  canRemove,
}: CustomerCardProps) {
  const isPrimary = customer.role === 'primary';
  const isBilling = customer.is_billing;

  return (
    <div className="p-4 bg-white border border-secondary-200 rounded-lg">
      <div className="flex items-start justify-between gap-4">
        {/* Customer Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-medium text-secondary-900">
              {customer.customer_name}
            </h4>
            <span
              className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${getRoleBadgeColor(
                customer.role as ProjectCustomerRole,
                customer.is_billing
              )}`}
            >
              {getRoleLabel(customer.role as ProjectCustomerRole)}
            </span>
            {isBilling && customer.role !== 'billing' && (
              <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">
                Billing
              </span>
            )}
          </div>
          <div className="mt-1 text-sm text-secondary-500 space-y-0.5">
            {customer.email && (
              <p>
                <a
                  href={`mailto:${customer.email}`}
                  className="text-primary-600 hover:text-primary-700"
                >
                  {customer.email}
                </a>
              </p>
            )}
            {customer.phone && (
              <p>
                <a
                  href={`tel:${customer.phone}`}
                  className="text-primary-600 hover:text-primary-700"
                >
                  {customer.phone}
                </a>
              </p>
            )}
          </div>
        </div>

        {/* Actions */}
        {!disabled && (
          <div className="flex items-center gap-1">
            {!isPrimary && (
              <button
                onClick={onSetPrimary}
                disabled={isUpdating}
                className="p-1.5 text-secondary-400 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                title="Set as primary contact"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                  />
                </svg>
              </button>
            )}
            {!isBilling && (
              <button
                onClick={onSetBilling}
                disabled={isUpdating}
                className="p-1.5 text-secondary-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                title="Set as billing contact"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              </button>
            )}
            {canRemove && (
              <button
                onClick={onRemoveClick}
                disabled={isUpdating}
                className="p-1.5 text-secondary-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                title="Remove from project"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Remove Confirmation */}
      {isConfirmingRemove && (
        <div className="mt-3 pt-3 border-t border-secondary-200">
          <p className="text-sm text-secondary-700 mb-2">
            Remove {customer.customer_name} from this project?
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onRemoveCancel}
              disabled={isUpdating}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={onRemoveConfirm}
              disabled={isUpdating}
              isLoading={isUpdating}
              className="bg-red-600 hover:bg-red-700"
            >
              Remove
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
